import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { api } from '@/lib/api';
import { getAuthToken, setAuthToken, clearAuthToken } from '@/lib/tokenStorage';
import logger from '@/lib/logger';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
    } catch (error) {
      // Token might be invalid
      console.error('Auth /me fetch failed:', error);
      setUser(null);
      setToken(null);
      clearAuthToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const storedToken = getAuthToken();
    if (storedToken) {
      setToken(storedToken);
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [fetchUser]);

  const login = async (email, password, totp_code, recovery_code) => {
    try {
      const payload = { email, password };
      if (totp_code) payload.totp_code = totp_code;
      if (recovery_code) payload.recovery_code = recovery_code;
      const response = await api.post('/auth/login', payload);
      
      // Check if 2FA is required
      if (response.data?.requires_2fa) {
        return { success: false, requires_2fa: true };
      }
      
      const { token: newToken, user: userData } = response.data;
      
      setToken(newToken);
      setUser(userData);
      setAuthToken(newToken);
      
      return { success: true, user: userData };
    } catch (error) {
      const data = error.response?.data;
      const detail = data?.detail;
      // iter250 — the activation gate answers 403 with a structured detail
      // object. Surface it as its own outcome so the login screen can route
      // to /activate instead of printing "[object Object]" as an error.
      if (detail && typeof detail === 'object' && !Array.isArray(detail)
          && detail.code === 'email_not_verified') {
        return {
          success: false,
          activation_required: true,
          email: detail.email || email,
          can_resend: detail.can_resend !== false,
          pending_email_claim: !!detail.pending_email_claim,
          error: detail.message,
        };
      }
      const message = detail || data?.message || 'Login failed';
      const errorMsg = Array.isArray(message)
        ? message.map(e => e.msg).join(', ')
        : (typeof message === 'string' ? message : 'Login failed');
      return { success: false, error: errorMsg };
    }
  };

  const register = async (userData) => {
    try {
      const response = await api.post('/auth/register', userData);
      const { token: newToken, user: newUser } = response.data;

      // iter250 — a new signup gets NO session. The account is inert until
      // the activation link is clicked, so there is nothing to store and
      // nowhere to navigate except the "check your inbox" screen.
      if (!newToken) {
        return {
          success: true,
          activation_required: true,
          email: response.data?.email || userData.email,
          // iter251 — preview hosts only (see dev_turnstile_bypass_active).
          // Absent in prod.
          dev_activation_url: response.data?.dev_activation_url || null,
        };
      }

      setToken(newToken);
      setUser(newUser);
      setAuthToken(newToken);
      
      return { success: true, user: newUser };
    } catch (error) {
      const data = error.response?.data;
      const message = data?.detail || data?.message || 'Registration failed';
      // Handle Pydantic validation error arrays
      const errorMsg = Array.isArray(message) 
        ? message.map(e => e.msg).join(', ') 
        : (typeof message === 'string' ? message : 'Registration failed');
      return { success: false, error: errorMsg };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    clearAuthToken();
  };

  const refreshUser = useCallback(async () => {
    try {
      const response = await api.get('/auth/me');
      setUser(response.data);
      return response.data;
    } catch (err) {
      logger.debug('refreshUser failed:', err);
      return null;
    }
  }, []);

  const value = {
    user,
    setUser,
    refreshUser,
    token,
    loading,
    login,
    register,
    logout,
    isAuthenticated: !!token && !!user,
    isAdmin: user?.role === 'admin',
    isTrialExpired: user?.subscription_status === 'expired',
    isTrialActive: user?.subscription_status === 'trial',
    daysRemaining: (() => {
      if (user?.subscription_status !== 'trial' || !user?.trial_end) return 0;
      const end = new Date(user.trial_end);
      const now = new Date();
      return Math.max(0, Math.ceil((end - now) / (1000 * 60 * 60 * 24)));
    })(),
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
